import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '../types/auth';
import { storage, ACCESS_TOKEN_KEY } from '../utils/storage';

function normalizeUser(u: any): User | null {
  if (!u) return null;
  const normalized = {
    ...u,
    id: String(u.id ?? u.userId ?? u.id ?? ''),
    email: u.email ?? '',
    active: typeof u.active !== 'undefined' ? Number(u.active) : undefined,
  };
  return normalized;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  authChecked: boolean;
  hasSeenGuidelines: boolean;
  hasSeenLabelNotice: boolean;
}

interface AuthActions {
  setAuth: (token: string, user: User) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  logout: () => void;
  setUser: (user: User) => void;
  setHasSeenGuidelines: (val: boolean) => void;
  setHasSeenLabelNotice: (val: boolean) => void;
  setAuthChecked: (checked: boolean) => void;
}

const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
  authChecked: false,
  hasSeenGuidelines: false,
  hasSeenLabelNotice: false,
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setAuth: (token, user) => {
        const normalized = normalizeUser(user);
        if (!normalized) return;
        storage.set(ACCESS_TOKEN_KEY, token);
        storage.set('USER_ID', normalized.id);
        storage.set('USER_DATA', normalized);
        set({
          token,
          user: normalized,
          loading: false,
          error: null,
          authChecked: true,
        });
      },

      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error, loading: false }),
      clearError: () => set({ error: null }),

      logout: () => {
        storage.remove(ACCESS_TOKEN_KEY);
        storage.remove('USER_ID');
        storage.remove('USER_DATA');
        set({
          user: null,
          token: null,
          loading: false,
          error: null,
          authChecked: true,
        });
      },

      setUser: (newUser) => {
        const currentUser = get().user;
        const normalized = normalizeUser({ 
          ...(currentUser || {}), 
          ...newUser,
          // Explicitly preserve active status if not provided in the new update
          active: newUser.active !== undefined ? newUser.active : currentUser?.active 
        });
        if (!normalized) return;
        storage.set('USER_DATA', normalized);
        set({ user: normalized });
      },

      setHasSeenGuidelines: (hasSeenGuidelines) => set({ hasSeenGuidelines }),
      setHasSeenLabelNotice: (hasSeenLabelNotice) => set({ hasSeenLabelNotice }),

      setAuthChecked: (authChecked) => set({ authChecked }),
    }),
    {
      name: 'parcelpicks-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        hasSeenGuidelines: state.hasSeenGuidelines,
        hasSeenLabelNotice: state.hasSeenLabelNotice,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          storage.set(ACCESS_TOKEN_KEY, state.token);
          if (state.user) {
            storage.set('USER_ID', String(state.user.id ?? ''));
            storage.set('USER_DATA', state.user);
          }
        }
        useAuthStore.getState().setAuthChecked(true);
      },
    }
  )
);

// Selectors – expose normalized user so UI never sees undefined fields
export const selectUser = (state: AuthState & AuthActions) =>
  normalizeUser(state.user);
export const selectToken = (state: AuthState & AuthActions) => state.token;
export const selectIsAuthenticated = (state: AuthState & AuthActions) =>
  !!state.token;
export const selectLoading = (state: AuthState & AuthActions) => state.loading;
export const selectError = (state: AuthState & AuthActions) => state.error;
export const selectAuthChecked = (state: AuthState & AuthActions) =>
  state.authChecked;
